<template>
	<div class="p-p-2 child-content">
		<DataTable
			:value="deleteReasons"
			:loading="loading"
			class="p-datatable-sm"
			:autoLayout="true"
			columnResizeMode="fit"
			:scrollable="true"
			scrollHeight="flex"
			:rowHover="true"
			@row-dblclick="openEditDialog($event.data)"
			@row-reorder="onRowReorder"
		>
			<template #loading>
				{{ $t('Squeeze.General.Loading') }}
			</template>
			<template #empty>
				{{ $t('Squeeze.General.NoEntriesFound', { entryName: $t('Squeeze.DocumentClasses.DeleteReasons') }) }}
			</template>

			<Column :rowReorder="true" style="min-width: 2rem; max-width: 2rem;" />
			<Column field="name" :header="$t('Squeeze.General.Name')" style="min-width: 12rem;" />
			<Column field="translatedDescription" :header="$t('Squeeze.DocumentClasses.Description')" style="min-width: 16rem;">
				<template #body="slotProps">
					<i v-if="slotProps.data.translatedDescriptionSource === 'default'" v-tooltip="$t('Squeeze.System.BuiltInValue')" class="mdi mdi-lock-outline" style="fontSize: 1rem"></i>
					<RouterLink v-else-if="slotProps.data.translatedDescriptionSource === 'sqz' || slotProps.data.translatedDescriptionSource === 'auto'" class="translation-link" :to="{ name: 'DocumentClassTranslationsSubView', params: { documentClassId }, query: { focusKey: getReasonTranslationKey(slotProps.data.name) } }" v-tooltip="$t('Squeeze.System.Translations')">
						<i  class="mdi mdi-file-replace-outline" style="fontSize: 1rem"></i>
					</RouterLink>
					{{ slotProps.data.translatedDescription }}
				</template>
			</Column>
			<Column field="textfield" :header="$t('Squeeze.DocumentClasses.CommentField')" style="min-width: 10rem;">
				<template #body="slotProps">
					<Checkbox
						:modelValue="slotProps.data.textfield"
						:binary="true"
						@update:modelValue="onToggleTextfield(slotProps.data, $event)"
					/>
				</template>
			</Column>
			<Column field="active" :header="$t('Squeeze.DocumentClasses.Active')" style="min-width: 8rem;">
				<template #body="slotProps">
					<Checkbox
						:modelValue="slotProps.data.active"
						:binary="true"
						@update:modelValue="onToggleActive(slotProps.data, $event)"
					/>
				</template>
			</Column>
			<Column style="min-width: 7rem; max-width: 7rem; text-align: right;">
				<template #body="slotProps">
					<Button v-tooltip.bottom="$t('Squeeze.Locators.Edit')" icon="mdi mdi-pencil-outline" class="p-button-lg p-button-rounded p-button-success p-button-text" @click="openEditDialog(slotProps.data)" />
					<Button v-if="!slotProps.data.systemDefault" v-tooltip.bottom="$t('Squeeze.Validation.Buttons.Delete')" icon="mdi mdi-delete-outline" class="p-button-lg p-button-rounded p-button-danger p-button-text" @click="openDeleteDialog(slotProps.data)" />
				</template>
			</Column>

			<template #footer>
				<Button :label="$t('Squeeze.General.NewEntry')" type="button" icon="mdi mdi-plus" class="p-button" @click="openNewDialog" />
			</template>
		</DataTable>
	</div>

	<DialogDelete
		:showDialog="deleteDialog"
		@onClose="deleteDialog = false"
		@onConfirm="deleteReason"
	/>

	<EntryDialog
		:show="showDialog"
		@onClose="closeEntryDialog"
		@onConfirm="saveReason"
		:loading="loadingDialog"
		:headerText="headerText"
		:showKeepDialogOpen="!isEditMode"
	>
		<template #content>
			<BlockUI :blocked="loadingDialog">
				<div class="p-fluid p-formgrid p-grid p-input-filled">
					<div class="p-field p-col-12">
						<label for="reasonName">{{ $t('Squeeze.General.Name') }} *</label>
						<InputText
							id="reasonName"
							v-model="entry.name"
							:disabled="disableNameEdit"
							:class="{'p-invalid': isEntryInvalid() && showValidationMessage}"
						/>
						<small v-if="isEntryInvalid() && showValidationMessage" class="p-error">
							{{ $t('Forms.Val.CharactersWithoutSpecialCharacters', { field: $t('Squeeze.General.Name')}) }}
						</small>
					</div>
					<div class="p-field p-col-12">
						<label for="reasonDescription">{{ $t('Squeeze.DocumentClasses.Description') }}</label>
						<DexInputText
							id="reasonDescription"
							v-model.trim="entry.description"
							:translatedDescription="descriptionLockValue"
							:disableTranslationUrl="descriptionDisableUrl"
							:translationKey="descriptionTranslationKey"
						/>
					</div>
					<div class="p-field-checkbox p-col-12">
						<Checkbox id="reasonTextfield" v-model="entry.textfield" :binary="true" />
						<label for="reasonTextfield">{{ $t('Squeeze.DocumentClasses.CommentField') }}</label>
					</div>
					<div class="p-field-checkbox p-col-12">
						<Checkbox id="reasonActive" v-model="entry.active" :binary="true" />
						<label for="reasonActive">{{ $t('Squeeze.DocumentClasses.Active') }}</label>
					</div>
				</div>
			</BlockUI>
		</template>
	</EntryDialog>
</template>

<script lang="ts">
import {getTranslatedDescription} from "@/util/TranslationHelper";
import {computed, defineComponent, onMounted, reactive, ref} from "vue";
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import InputText from "primevue/inputtext";
import Checkbox from "primevue/checkbox";
import Tooltip from "primevue/tooltip";
import BlockUI from "primevue/blockui";
import DialogDelete from "@/components/DialogDelete.vue";
import DexInputText from "@/components/DexInputText.vue";
import EntryDialog from "@/components/EntryDialog.vue";
import {ToastManager} from "@/util/ToastManager";
import {ClientManager} from "@/singletons/ClientManager";
import {DEFAULT_LOCALE} from "@/lang";
import type { DocumentClassDeleteReason } from "@dex/squeeze-client-ts";

interface DocumentClassDeleteReasonRow extends DocumentClassDeleteReason {
	id: number;
	name: string;
	translatedDescription: string;
	translatedDescriptionSource: string;
	textfield: boolean;
	sortOrder: number;
	active: boolean;
	systemDefault?: boolean;
}

interface DeleteReasonEntry {
	name: string;
	description: string;
	textfield: boolean;
	sortOrder: number;
	active: boolean;
}

interface RowReorderEvent {
	value?: DocumentClassDeleteReasonRow[];
}

export default defineComponent({
	name: 'DocumentClassDeleteReasonsSubView',
	components: {
		DataTable,
		Column,
		InputText,
		Checkbox,
		BlockUI,
		DialogDelete,
		DexInputText,
		EntryDialog,
	},
	directives: {
		'tooltip': Tooltip,
	},
	props: {
		documentClassId: {
			type: Number,
			default: 0,
		},
	},
	setup(props) {
		const { t, te, locale } = useI18n();
		const toast = useToast();
		const documentClassApi = ClientManager.getInstance().squeeze.documentClass;

		const loading = ref<boolean>(false);
		const loadingDialog = ref<boolean>(false);
		const deleteDialog = ref<boolean>(false);
		const showDialog = ref<boolean>(false);
		const showValidationMessage = ref<boolean>(false);
		const isEditMode = ref<boolean>(false);
		const originalReasonId = ref<number | null>(null);
		const disableNameEdit = ref<boolean>(false);

		const headerText = ref<string>(t('Squeeze.General.NewEntry'));
		const deleteReasons = ref<DocumentClassDeleteReasonRow[]>([]);
		const reasonToDelete = ref<DocumentClassDeleteReasonRow | null>(null);
		const reasonNameRegex = /^[a-zA-Z0-9_-]{3,45}$/;

		/**
		 * Name of the current document class, needed to build the translation key of a delete reason description.
		 * Derived from the backend-generated translation key of the loaded reasons, so no extra request is required.
		 */
		const documentClassName = ref<string>('');

		/**
		 * Existing translation for the description shown in the edit dialog.
		 * When set, DexInputText locks the field and shows the translation indicator.
		 */
		const descriptionLockValue = ref<string>('');

		/** Whether the translation indicator should only inform (built-in value) instead of linking to the translation page. */
		const descriptionDisableUrl = ref<boolean>(false);

		const entry = reactive<DeleteReasonEntry>({
			name: '',
			description: '',
			textfield: false,
			sortOrder: 0,
			active: true,
		});

		/** Builds the translation key of a reason description, used to focus the matching row on the translation page. */
		const getReasonTranslationKey = (reasonName: string): string => {
			if (!documentClassName.value || !reasonName) {
				return '';
			}
			return `documentclasses.${documentClassName.value}.deletionreasons.${reasonName}.description`;
		};

		/** Translation key for the reason currently open in the edit dialog. */
		const descriptionTranslationKey = computed<string>(() => getReasonTranslationKey(entry.name));

		const resetEntry = () => {
			entry.name = '';
			entry.description = '';
			entry.textfield = false;
			entry.sortOrder = deleteReasons.value.length;
			entry.active = true;
			showValidationMessage.value = false;
			originalReasonId.value = null;
			disableNameEdit.value = false;
			descriptionLockValue.value = '';
			descriptionDisableUrl.value = false;
		}

		const closeEntryDialog = () => {
			showDialog.value = false;
			resetEntry();
		}

		const isEntryInvalid = (): boolean => {
			return !reasonNameRegex.test(entry.name);
		}
		const getTranslatedReasonDescription = (reason: DocumentClassDeleteReason): string => {
			return te('Squeeze.Validation.Dialogs.DeleteDocument.Reasons.' + reason.name) ? t('Squeeze.Validation.Dialogs.DeleteDocument.Reasons.' + reason.name) : getTranslatedDescription(reason) || reason.name || ''
		}
		const getTranslatedReasonSource = (reason: DocumentClassDeleteReason): string => {
			return te('Squeeze.Validation.Dialogs.DeleteDocument.Reasons.' + reason.name) ? 'default' : reason.translatedDescription ? 'sqz' : 'auto' ?? ''
		}

		const reloadData = () => {
			loading.value = true;

			documentClassApi.getDocumentClassDeleteReasons(props.documentClassId)
				.then((data: DocumentClassDeleteReason[]) => {
					// Derive the document class name from any reason's translation key
					// (documentclasses.<className>.deletionreasons.<name>.description) to avoid an extra request.
					const sampleKey = data.find((reason: DocumentClassDeleteReason) => reason.translationKey)?.translationKey ?? '';
					documentClassName.value = sampleKey.match(/^documentclasses\.(.+?)\.deletionreasons\./)?.[1] ?? '';

					deleteReasons.value = data.map((reason: DocumentClassDeleteReason) => ({
						id: Number(reason.id ?? 0),
						name: String(reason.name ?? ''),
						translatedDescription: getTranslatedReasonDescription(reason),
						translatedDescriptionSource: getTranslatedReasonSource(reason),
						textfield: Boolean(reason.textfield),
						sortOrder: Number(reason.sortOrder ?? 0),
						active: Boolean(reason.active),
						systemDefault: reason.systemDefault,
					})).sort((a, b) => a.sortOrder - b.sortOrder);
				})
				.catch((response: Response) => response.json().then((error: { message: string }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), `${t('Squeeze.General.Error')}: ${error.message}`);
				}).catch(() => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error'));
				}))
				.finally(() => {
					loading.value = false;
				});
		}

		/**
		 * Stores the entered description as a translation of the delete reason.
		 * The value is written for the current user language and the default language ("English US" / "en").
		 * @param reasonName Name of the delete reason (part of the translation key)
		 * @param description Description entered by the user
		 */
		const writeDescriptionTranslation = async (reasonName: string, description: string) => {
			if (!documentClassName.value || !description.trim()) {
				return;
			}

			const translationKey = getReasonTranslationKey(reasonName);
			const languages = Array.from(new Set([String(locale.value), DEFAULT_LOCALE]));
			const payload = {
				translationKey,
				translations: languages.map((lang: string) => ({lang, translation: description})),
			};

			const response = await ClientManager.getInstance().squeeze.translations.postTranslation(payload);

			if (response.status !== 204) {
				const responseJSON = await response.json() as { message: string };
				throw new Error(responseJSON.message);
			}
		}

		const openNewDialog = () => {
			isEditMode.value = false;
			headerText.value = t('Squeeze.General.NewEntry');
			resetEntry();
			showDialog.value = true;
		}

		const openEditDialog = (row: DocumentClassDeleteReasonRow) => {
			isEditMode.value = true;
			headerText.value = t('Squeeze.Locators.Edit');
			entry.name = row.name;
			entry.textfield = row.textfield;
			entry.sortOrder = row.sortOrder;
			entry.active = row.active;
			originalReasonId.value = row.id;
			disableNameEdit.value = row.systemDefault === true;
			showValidationMessage.value = false;

			// Lock the description when a translation exists: 'sqz' links to the translation page,
			// 'default' (built-in value) only shows an info indicator, otherwise the field stays editable.
			const hasTranslation = row.translatedDescriptionSource === 'sqz' || row.translatedDescriptionSource === 'default';
			entry.description = hasTranslation ? row.translatedDescription : '';
			descriptionLockValue.value = hasTranslation ? row.translatedDescription : '';
			descriptionDisableUrl.value = row.translatedDescriptionSource === 'default';

			showDialog.value = true;
		}

		const openDeleteDialog = (row: DocumentClassDeleteReasonRow) => {
			reasonToDelete.value = row;
			deleteDialog.value = true;
		}

		const saveReason = (keepDialogOpen = false) => {
			showValidationMessage.value = true;
			if (isEntryInvalid()) {
				return;
			}

			loadingDialog.value = true;
			loading.value = true;
			let promise = null;
			if (isEditMode.value) {
				const putRequest: {
					name: string;
					textfield: boolean;
					sortOrder: number;
					active: boolean;
				} = {
					name: entry.name,
					textfield: entry.textfield,
					sortOrder: entry.sortOrder,
					active: entry.active,
				};

				if (originalReasonId.value === null) {
					loadingDialog.value = false;
					loading.value = false;
					return;
				}

				promise = documentClassApi.putDocumentClassDeleteReason(props.documentClassId, originalReasonId.value, putRequest);
			} else {
				promise = documentClassApi.postDocumentClassDeleteReason(props.documentClassId, {
					name: entry.name,
					textfield: entry.textfield,
					sortOrder: deleteReasons.value.length,
					active: entry.active,
				});
			}

			// Persist the description as a translation only when the field is editable (no translation locked yet)
			// and a value was entered. This covers the initial creation and adding a description to an existing reason.
			const shouldWriteTranslation = !descriptionLockValue.value && entry.description.trim() !== '';
			const reasonName = entry.name;
			const reasonDescription = entry.description;

			promise.then(() => {
				if (shouldWriteTranslation) {
					// The reason is already saved / a failing translation write must not abort the flow,
					// so it is handled here with its own toast instead of the reason-save error handler below.
					return writeDescriptionTranslation(reasonName, reasonDescription).catch((error: unknown) => {
						const message = error instanceof Error ? error.message : '';
						ToastManager.showError(toast, t('Squeeze.General.Error'), message ? `${t('Squeeze.General.Error')}: ${message}` : t('Squeeze.General.Error'));
					});
				}
			}).then(() => {
				reloadData();
				if (isEditMode.value || !keepDialogOpen) {
					showDialog.value = false;
				}
				resetEntry();
			}).catch((response: Response) => response.json().then((error: { message: string }) => {
				ToastManager.showError(toast, t('Squeeze.General.Error'), `${t('Squeeze.General.Error')}: ${error.message}`);
			}).catch(() => {
				ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error'));
			})).finally(() => {
				loadingDialog.value = false;
				loading.value = false;
			});
		}

		const updateSingleField = (row: DocumentClassDeleteReasonRow, field: "active" | "textfield", value: boolean) => {
			const previousValue = row[field];
			row[field] = value;
			documentClassApi.putDocumentClassDeleteReason(props.documentClassId, row.id, {
				name: row.name,
				textfield: field === "textfield" ? value : row.textfield,
				sortOrder: row.sortOrder,
				active: field === "active" ? value : row.active,
			}).catch((response: Response) => response.json().then((error: { message: string }) => {
				row[field] = previousValue;
				ToastManager.showError(toast, t('Squeeze.General.Error'), `${t('Squeeze.General.Error')}: ${error.message}`);
			}).catch(() => {
				row[field] = previousValue;
				ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error'));
			}));
		}

		const onToggleActive = (row: DocumentClassDeleteReasonRow, value: boolean) => {
			updateSingleField(row, "active", value);
		}

		const onToggleTextfield = (row: DocumentClassDeleteReasonRow, value: boolean) => {
			updateSingleField(row, "textfield", value);
		}

		const onRowReorder = (event: RowReorderEvent) => {
			if (!event.value) {
				return;
			}

			deleteReasons.value = event.value.map((reason: DocumentClassDeleteReasonRow, index: number) => ({
				...reason,
				sortOrder: index,
			}));

			const orderedList = deleteReasons.value.map((reason: DocumentClassDeleteReasonRow) => Number(reason.id));

			loading.value = true;
			documentClassApi.putDocumentClassDeleteReasonSortOrder(props.documentClassId, {elements: orderedList})
				.then(() => {
					reloadData();
				})
				.catch((response: Response) => response.json().then((error: { message: string }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), `${t('Squeeze.General.Error')}: ${error.message}`);
				}).catch(() => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error'));
				}))
				.finally(() => {
					loading.value = false;
				});
		}

		const deleteReason = () => {
			if (!reasonToDelete.value) {
				return;
			}

			loading.value = true;
			documentClassApi.deleteDocumentClassDeleteReason(props.documentClassId, reasonToDelete.value.id)
				.then(() => {
					reloadData();
				})
				.catch((response: Response) => response.json().then((error: { message: string }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), `${t('Squeeze.General.Error')}: ${error.message}`);
				}).catch(() => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error'));
				}))
				.finally(() => {
					deleteDialog.value = false;
					reasonToDelete.value = null;
					loading.value = false;
				});
		}

		onMounted(() => {
			reloadData();
		});

		return {
			deleteReasons,
			loading,
			loadingDialog,
			deleteDialog,
			showDialog,
			showValidationMessage,
			isEditMode,
			disableNameEdit,
			headerText,
			entry,
			descriptionLockValue,
			descriptionDisableUrl,
			descriptionTranslationKey,
			getReasonTranslationKey,
			isEntryInvalid,
			openNewDialog,
			openEditDialog,
			openDeleteDialog,
			closeEntryDialog,
			saveReason,
			deleteReason,
			onToggleActive,
			onToggleTextfield,
			onRowReorder,
		};
	},
});
</script>

<style scoped>

.child-content {
	height: calc(100vh - 5.6rem - 4.1rem);
}

::v-deep(i.p-datatable-reorderablerow-handle.pi.pi-bars) {
	cursor: grab;
}

::v-deep(i.p-datatable-reorderablerow-handle.pi.pi-bars:active) {
	cursor: grabbing;
}

a.translation-link,
a.translation-link:visited,
a.translation-link:hover {
	color: var(--dex-primary-color, #2196f3);
	text-decoration: none;
}

</style>
